import React, { Component } from 'react';
import { get } from 'lodash';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import PropTypes from 'prop-types';
import SearchIcon from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from 'fuse-ui/components/Typography';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import opacity from '../common/theme/colors/opacity';
import { withStyles } from '@material-ui/core/styles';

const styles = ( theme ) => ( {
    columnFilterDropdown: {
        paddingBottom: 0
    },
    columnList: {
        '&:focus': {
            outline: 'none'
        },
        maxHeight: 185,
        overflow: 'auto',
        paddingTop: 0,
        position: 'relative',
        width: '100%',
    },
    columnListItem: {
        backgroundColor: theme.palette.background.paper,
        height: 32,
        paddingLeft: theme.spacing.unit,
        paddingRight: theme.spacing.unit,
    },
    columnReset: {
        '&:focus': {
            outline: 'none'
        },
        cursor: 'pointer',
        padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    },
    columnSearch: {
        '&:focus': {
            outline: 'none'
        },
        minWidth: 200,
    },
    columnSearchInput: {
        height: 36,
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
    },
    divider: {
        marginBottom: theme.spacing.unit,
        marginTop: 0,
    },
    iconButton: {
        '&:active': {
            background: opacity.black14,
        },
        marginRight: -theme.spacing.unit,
    },
    menu: {
        minWidth: 280
    },
    searchIcon: {
        color: opacity.black38,
    },
    selectedCountRow: {
        height: 48,
        padding: `14px ${theme.spacing.unit * 2}px`,
    }
} );

class ColumnFilter extends Component{
    state = {
        anchorEl: null,
        searchField: ''
    };

    buildColumnFilterList = () => {
        const { classes, columnHeaders, persistColumnFilters } = this.props;

        return columnHeaders
            .filter( this.filterPredicate )
            .map( col => {
                const isHidden = get( col, 'hide', true );
                const hideColumn = col.type.includes( 'hiddenGroupColumn' );
                if( persistColumnFilters.includes( col.field ) ){
                    return (
                        <ListItem
                            classes={{ root: classes.columnListItem }}
                            data-automation-id={`column-filter-list-item-${col.field}`}
                            disabled
                            key={col.field}
                        >
                            <Checkbox checked={!isHidden || hideColumn} disabled />
                            <ListItemText primary={col.headerName} />
                        </ListItem>
                    );
                }
                else{
                    return (
                        <ListItem
                            button
                            classes={{ root: classes.columnListItem }}
                            data-automation-id={`column-filter-list-item-${col.field}`}
                            key={col.field}
                            onClick={this.handleColumnToggle( col.field )}
                        >
                            <Checkbox color="primary" checked={!isHidden} disableRipple />
                            <ListItemText primary={col.headerName} />
                        </ListItem>
                    );
                }
            } );
    }

    filterPredicate = ( col ) => {
        const { searchField } = this.state;
        return !searchField || col.headerName.toLowerCase().includes( searchField.toLowerCase() );
    }

    handleOpenMenu = event => {
        this.setState( { anchorEl: event.currentTarget } );
    };

    handleClose = () => {
        this.setState( { anchorEl: null, searchField: '' } );
    };

    handleColumnToggle = value => () => {
        this.props.onColumnToggle( value );
        this.setState( {
            searchField: ''
        } );
    };

    handleColumnReset = () => {
        this.props.onColumnReset();
        this.setState( {
            searchField: ''
        } );
    }

    handleSearchInput = ( event ) => {
        this.setState( {
            searchField: event.target.value
        } );
    }

    render(){
        const { anchorEl, searchField } = this.state;
        const { classes, columnHeaders } = this.props;
        const columnsSelected = columnHeaders.filter( col => col.type.includes( 'hiddenGroupColumn' ) || col.hide === false ).length;

        return (
            <React.Fragment>
                <Tooltip title="Columns" placement="top">
                    <IconButton
                        className={classes.iconButton}
                        onClick={this.handleOpenMenu}
                        data-automation-id="column-filter-button"
                        style={{ background: Boolean( anchorEl ) ? opacity.black14 : null }}
                    >
                        <ViewColumnIcon />
                    </IconButton >
                </Tooltip>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean( anchorEl )}
                    onClose={this.handleClose}
                    anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'bottom',
                    }}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top',
                    }}
                    getContentAnchorEl={null}
                    MenuListProps={{ disablePadding: true }}
                    classes={{ paper: classes.menu }}
                >
                    <TextField
                        placeholder="Search Columns"
                        fullWidth={true}
                        classes={{ root: classes.columnSearch }}
                        onChange={this.handleSearchInput}
                        InputProps={{
                            classes: {
                                root: classes.columnSearchInput
                            },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className={classes.searchIcon} />
                                </InputAdornment>
                            ),
                        }}
                        type="search"
                        data-automation-id="column-filter-search-field"
                        value={searchField}
                    />
                    <Typography
                        className={classes.columnReset}
                        color="primary"
                        data-automation-id="column-filter-reset-button"
                        onClick={this.handleColumnReset}
                        variant="button"
                    >
                        Reset to Default
                    </Typography>
                    <Divider className={classes.divider} />
                    <List classes={{ root: classes.columnList }}>
                        { this.buildColumnFilterList() }
                    </List>
                    <Typography classes={{ root: classes.selectedCountRow }} >
                        {`${columnsSelected} Selected`}
                    </Typography>
                </Menu>
            </React.Fragment>
        );
    }
}

ColumnFilter.propTypes = {
    classes: PropTypes.object.isRequired,
    columnHeaders: PropTypes.array,
    onColumnReset: PropTypes.func,
    onColumnToggle: PropTypes.func,
    persistColumnFilters: PropTypes.array,
};

ColumnFilter.defaultProps = {
    onColumnReset: () => {},
    onColumnToggle: () => {},
    persistColumnFilters: [],
};

export default withStyles( styles )( ColumnFilter );
